import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../../../../components/ui/PageTitle";
import LoadingIndicator from "../../../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../../../components/ui/ErrorMessage";
import Button from "../../../../../components/ui/Button";
import ConfirmationModal from "../../../../../components/ui/ConfirmationModal";
import Input from "../../../../../components/ui/Input";
import TextArea from "../../../../../components/ui/Textarea";
import { getComboDetails, updateAdminCombo, deleteAdminCombo } from "../../../../../lib/apiClient";
import { ROUTES } from "../../../../../lib/constants";

import { Combo } from "../../../../../types";

export default function AdminComboEditPage() {
  const router = useNavigate();
  const params = useParams();
  const comboId = params.id as string;

  const [combo, setCombo] = useState<Combo | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [markupPrice, setMarkupPrice] = useState<string>("");
  const [discountedPrice, setDiscountedPrice] = useState<string>("");
  const [validityDays, setValidityDays] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [ispost, setIspost] = useState<boolean>();

  // Channels for selector

  const fetchCombo = useCallback(async () => {
    setIsLoadingData(true);
    setError(null);
    try {
      const data = await getComboDetails(comboId);
      setCombo(data as unknown as Combo);
      setName(data.name || "");
      setMarkupPrice((data as any).price?.toString() ?? "");
      setDiscountedPrice((data as any).discounted_price !== undefined ? String((data as any).discounted_price) : "");
      setValidityDays(String((data as any).duration_days ?? (data as any).validity_days ?? 0));
      setDescription(data.description || "");
      setSelectedChannelIds((data as any).included_channels?.map((c: any) => c._id) || (data as any).channel_ids || []);
      setIspost(data.is_post)
    } catch (e: any) {
      setError(e?.message || "Failed to load combo");
    } finally {
      setIsLoadingData(false);
    }
  }, [comboId]);



  useEffect(() => {
    fetchCombo();
  }, [fetchCombo]);

  const validate = (): boolean => {
    const errors: string[] = [];
    if (!name.trim()) errors.push("Name is required");
    const mp = markupPrice === "" ? null : Number(markupPrice);
    if (markupPrice !== "" && (mp === null || isNaN(mp) || (mp !== null && mp < 0))) {
      errors.push("Markup price must be a non-negative number");
    }
    const dp = discountedPrice === "" ? null : Number(discountedPrice);
    if (dp !== null) {
      if (isNaN(dp) || dp < 0) errors.push("Discounted price must be non-negative");
      if (mp !== null && dp !== null && dp >= (mp as number)) {
        errors.push("Discounted price must be less than markup price");
      }
    }
    const vd = Number(validityDays);
    if (!vd || isNaN(vd) || vd <= 0 || !Number.isInteger(vd)) {
      errors.push("Validity days must be an integer > 0");
    }
    if (selectedChannelIds.length === 0) errors.push("Select at least one channel");
    setFormError(errors[0] || null);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload: any = {
        name: name.trim(),
        price: (combo as any)?.price ?? undefined,
        validity_days: Number(validityDays),
        description: description.trim() || null,
        is_post: ispost
      };
      if (markupPrice !== "") payload.price = Number(markupPrice);
      if (discountedPrice !== "") payload.discounted_price = Number(discountedPrice);
      await updateAdminCombo(comboId, payload);
      router(ROUTES.ADMIN_COMBOS);
    } catch (e: any) {
      setFormError(e?.message || "Failed to update combo");
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await deleteAdminCombo(comboId);
      router(ROUTES.ADMIN_COMBOS);
    } catch (e: any) {
      setFormError(e?.message || "Failed to delete combo");
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <PageTitle title="Edit Combo" />
        <LoadingIndicator text="Loading combo data..." />
      </div>
    );
  }

  if (error || !combo) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <PageTitle title="Edit Combo" />
        <ErrorMessage title="Error Loading Combo" message={error || "Could not load combo data or ID is invalid."} />
        <div className="mt-4 space-x-2">
          <Button onClick={fetchCombo} variant="primary">Retry</Button>
          <Button variant="secondary" onClick={() => router(ROUTES.ADMIN_COMBOS)}>Back to Combo Plans</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 px-8">
        <PageTitle title={`Edit Combo: ${combo.name}`} subtitle={`ID: ${combo._id}`} />
        <div className="flex space-x-2 flex-shrink-0">
          <Button variant="secondary" onClick={() => router(ROUTES.ADMIN_COMBOS)} disabled={isSubmitting}>Back to List</Button>
          <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)} disabled={isSubmitting}>Delete Combo</Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-gradient-to-br from-dark-secondary via-dark-secondary to-dark-tertiary/20 p-8 rounded-xl shadow-2xl border border-dark-tertiary/60 backdrop-blur-sm">
        {/* 1. Basic Information */}
        <div className="space-y-6">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">1</span>
              </div>
              Basic Information
            </h2>
            <p className="text-text-secondary text-sm ml-11">Essential details about the combo plan</p>
          </div>

          <div className="ml-11 space-y-6">
            <Input
              label="Combo Name"
              id="combo_name"
              name="combo_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isSubmitting}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Markup Price (Required)"
                id="combo_markup_price"
                name="combo_markup_price"
                type="number"
                value={markupPrice}
                onChange={(e) => setMarkupPrice(e.target.value)}
                required
                min={0}
                disabled={isSubmitting}
              />
            </div>

            <Input
              label="Validity (Days)"
              id="combo_validity_days"
              name="combo_validity_days"
              type="number"
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
              required
              min={1}
              step={1}
              disabled={isSubmitting}
            />

            <TextArea
              label="Description (Optional)"
              id="combo_description"
              name="combo_description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* 2. Included Channels */}
        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">2</span>
              </div>
              Included Channels
            </h2>
            <p className="text-text-secondary text-sm ml-11">Channels included in this combo plan</p>
          </div>

          <div className="ml-11">
            <div className="border border-dark-border rounded-md bg-dark-secondary p-3 max-h-48 overflow-y-auto">
              {combo.included_channels && combo.included_channels.length > 0 ? (
                <ul className="space-y-1">
                  {combo.included_channels.map((channel: any) => (
                    <li
                      key={channel._id}
                      className="px-3 py-1 bg-dark-tertiary text-text-primary rounded-md hover:bg-dark-primary transition-colors"
                    >
                      {channel.name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-text-secondary text-sm">No channels included in this combo.</p>
              )}
            </div>
          </div>
        </div>

        {/* 3. Settings */}
        <div className="space-y-4 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">3</span>
              </div>
              Settings
            </h2>
            <p className="text-text-secondary text-sm ml-11">Control KYC post-payment settings</p>
          </div>

          <div className="ml-11 space-y-4">
            <label
              htmlFor="is-post"
              className="text-sm font-medium text-text-secondary flex items-center cursor-pointer"
            >
              <input
                type="checkbox"
                id="is-post"
                className="form-checkbox h-4 w-4 text-golden-accent rounded mr-2 focus:ring-golden-focus-ring bg-dark-tertiary border-dark-border checked:bg-golden-accent checked:border-transparent focus:ring-offset-dark-primary"
                checked={ispost}
                onChange={(e) => setIspost(e.target.checked)}
                disabled={isSubmitting}
              />
              Post Payment KYC
            </label>
            <p className="text-xs text-text-secondary">
              Post Payment flow for KYC verification.
            </p>
          </div>
        </div>

        {formError && <ErrorMessage message={formError} />}

        <div className="mt-6 flex gap-2 justify-center">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Update Combo
          </Button>
        </div>
      </form>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        message={
          <>
            Are you sure you want to delete the combo {""}
            <strong className="text-text-primary">{combo.name}</strong>?
          </>
        }
        confirmText="Yes, Delete Combo"
        confirmButtonVariant="danger"
        isLoading={isSubmitting && isDeleteModalOpen}
      />
    </div>
  );
}

